import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { getIssueTypeMap, updateShapeUpSettings } from '../../utils/shapeUpApi';

/**
 * Which Jira issue types count as scopes, and what a new scope is created as.
 *
 * Not every org shapes work into Stories. Some use a custom type literally named
 * "Scope"; others want only a couple of their types to reach the hill. This lists
 * the types the boards offer, lets a team tick the ones that are scopes, and picks
 * the type a hand-created scope becomes in Jira. Leaving everything ticked is the
 * original behaviour: every non-subtask child of an epic is a scope.
 */
const IssueTypeMap = ({ organizationId, canManage = false, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // null until edited, then a Set of lower-cased names that are ticked as scopes.
  const [checked, setChecked] = useState(null);
  const [defaultType, setDefaultType] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const result = await getIssueTypeMap(organizationId);
      setData(result);
      setChecked(null);
      setDefaultType(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not read the Jira issue types.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  // Non-subtask types are the only ones a scope can be; subtasks are the todo layer.
  const types = useMemo(
    () => (data?.issueTypes || []).filter(type => !type.subtask),
    [data]
  );

  // The set of names currently ticked, from the edited draft or the server's
  // isScopeType flags. Lower-cased throughout so comparisons never miss on case.
  const checkedSet = useMemo(() => {
    if (checked) return checked;
    const seeded = new Set();
    types.forEach(type => { if (type.isScopeType) seeded.add(type.name.toLowerCase()); });
    return seeded;
  }, [checked, types]);

  const selectedDefault = defaultType ?? data?.defaultIssueType ?? 'Story';

  const dirty = checked !== null || (defaultType !== null && defaultType !== data?.defaultIssueType);

  const toggle = (name) => {
    setChecked(current => {
      const next = new Set(current || checkedSet);
      const key = name.toLowerCase();
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Store the names in their display casing so the settings read back the way
      // a person typed them; the server normalizes for comparison. All ticked
      // means "no restriction", which is stored as an empty array.
      const all = types.length;
      const scopeIssueTypes = checkedSet.size === all
        ? []
        : types.filter(type => checkedSet.has(type.name.toLowerCase())).map(type => type.name);

      await updateShapeUpSettings(organizationId, {
        scopeIssueTypes,
        defaultIssueType: selectedDefault,
      });
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the issue types.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />;

  // Types that are ticked now but would be dropped: unchecking one re-scopes any
  // existing scopes of that type off the hill on the next sync, so it is worth a word.
  const droppedWithScopes = types.filter(
    type => !checkedSet.has(type.name.toLowerCase()) && type.scopeCount > 0
  );

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Which Jira issue types are scopes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A scope is a child of a bet&apos;s epic. Most teams use Stories, but yours may use a
        custom type such as &quot;Scope&quot;. Tick the types that belong on the hill and choose
        the one a new scope is created as. With everything ticked, every non-subtask child
        counts, which is the default.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {data?.jiraError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`Jira could not be read (${data.jiraError}), so this only lists types already
            seen on a scope. Connect Jira to see every type on the board.`}
        </Alert>
      )}

      {types.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No issue types yet. Sync a bet from a Jira epic and its types will appear here.
        </Typography>
      ) : (
        <>
          {data?.projectKeys?.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" variant="outlined" label={`Projects: ${data.projectKeys.join(', ')}`} />
            </Stack>
          )}

          <Stack divider={<Divider />} spacing={0}>
            {types.map((type) => {
              const key = type.name.toLowerCase();
              const isChecked = checkedSet.has(key);
              return (
                <Box key={key} sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    sx={{ minWidth: 200, mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        disabled={!canManage}
                        onChange={() => toggle(type.name)}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{type.name}</Typography>}
                  />
                  {type.scopeCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {type.scopeCount === 1 ? '1 scope' : `${type.scopeCount} scopes`}
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {!isChecked && type.scopeCount > 0 && (
                    <Chip size="small" color="warning" variant="outlined" label="would leave the hill" />
                  )}
                </Box>
              );
            })}
          </Stack>

          {droppedWithScopes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {`Unticking a type that already has scopes removes those scopes on the next
                sync, along with their position history. Only these types are scopes once
                you save.`}
            </Alert>
          )}

          <FormControl size="small" sx={{ mt: 2, minWidth: 220 }} disabled={!canManage}>
            <InputLabel id="issue-type-create-as">Create new scopes as</InputLabel>
            <Select
              labelId="issue-type-create-as"
              label="Create new scopes as"
              value={selectedDefault}
              onChange={(e) => setDefaultType(e.target.value)}
            >
              {/* The stored default may name a type not in this project's list, so
                  seed the options with it to keep the current choice selectable. */}
              {[...new Set([
                selectedDefault,
                ...types.map(type => type.name),
              ])].filter(Boolean).map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {canManage && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save issue types'}
              </Button>
              {dirty && (
                <Button color="inherit" onClick={() => { setChecked(null); setDefaultType(null); }}>
                  Discard
                </Button>
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default IssueTypeMap;
